// Engine-agnostic "is the cursor inside a routine/trigger compound body?"
// detector, used to suppress the full-statement SQL-snippet flood at a
// procedural body-start.
//
// Inside a `CREATE PROCEDURE/FUNCTION/TRIGGER … BEGIN … END` body (or an Oracle
// anonymous PL/SQL block) the `;` that terminates an inner statement makes the
// naive `statementTextBeforeCursor` splitter report an empty statement — so the
// snippet engine thinks the user is at a fresh top-level statement start and
// offers all ~45 builtin templates, burying the routine's declared locals and
// the keyword list below Monaco's virtualized fold. The locals + keywords are
// far more relevant inside a body, so the flood is suppressed there.
//
// Reuses the proven compound-block tracker (`feedBlockWord` / `shouldSuppressSplit`)
// that the document scanner uses for compound-block-aware statement splitting,
// so block classification and `BEGIN/END` depth bookkeeping stay in one place.

import type { DatabaseEngineId } from '../../../core/engines';
import {
  feedBlockWord,
  newBlockState,
  resetBlockState,
  shouldSuppressSplit,
} from '../../sql-grammar/document/compoundBlocks';
import { tokenize } from './tokenizer';

type TrackedEngine = 'mssql' | 'mysql' | 'oracle' | 'sqlite';

function trackedEngine(engineId: DatabaseEngineId): TrackedEngine | null {
  // Postgres routine bodies are dollar-quoted, which the scanner already
  // protects; bare `BEGIN/COMMIT` stay separate transaction statements — so PG
  // is never tracked (mirrors `blockTrackingEngine`).
  return engineId === 'mssql' || engineId === 'mysql' || engineId === 'oracle' || engineId === 'sqlite'
    ? engineId
    : null;
}

/**
 * True when `offset` lies inside an open routine/trigger compound block — i.e.
 * the current statement is classified as a routine body AND the `BEGIN/CASE/IF/
 * LOOP/…` nesting depth is greater than zero at the cursor.
 */
export function isInsideRoutineBodyBlock(
  text: string,
  offset: number,
  engineId: DatabaseEngineId,
): boolean {
  const engine = trackedEngine(engineId);
  if (!engine) return false;

  const st = newBlockState();
  const tokens = tokenize(text, 0, offset, engineId);
  for (const tok of tokens) {
    if (tok.start >= offset) break;
    if (tok.type === 'semicolon') {
      // A `;` only ends the statement (resetting block classification) when it
      // is NOT suppressed by an open routine body.
      if (!shouldSuppressSplit(st)) resetBlockState(st);
      continue;
    }
    if (tok.type === 'ident') {
      feedBlockWord(st, tok.text, engine);
    }
  }
  return st.isRoutineBody && st.depth > 0;
}

/**
 * PostgreSQL's routine bodies are dollar-quoted (`AS $$ … $$`, `DO $body$ …
 * $body$`), so the compound-block tracker above never sees them. This is the
 * PG answer to "where does the routine body the cursor is in start": the
 * offset just past an unterminated opening dollar tag before `offset`, or
 * `null` when the cursor is not inside one. Tags pair exactly, and a body's
 * own inner dollar quotes are rare enough that a single open tag is tracked.
 */
export function dollarQuotedBodyStart(text: string, offset: number): number | null {
  const re = /\$([A-Za-z_][A-Za-z0-9_]*)?\$/g;
  let open: { tag: string; end: number } | null = null;
  for (const m of text.slice(0, offset).matchAll(re)) {
    if (open === null) open = { tag: m[0], end: (m.index ?? 0) + m[0].length };
    else if (m[0] === open.tag) open = null;
  }
  return open === null ? null : open.end;
}

export function isInsideDollarQuotedBody(text: string, offset: number): boolean {
  return dollarQuotedBodyStart(text, offset) !== null;
}
